import { briefgFlowContentEnum, briefgFlowFlightStruct } from "./briefing-flow.model";
import { DatabaseService } from "../services/database/database.service";
import { FileSystemService } from "../services/file-system/file-system.service";
import { DOC_TEXT_SEARCH_PROCESSES } from "../db/catalog";

/** Match a chart's local extent centre to the finite route, including its endpoints. */
export function documentRoutePosition(point: number[], route: { geoPos?: number[]; trackDist: number }[]) {
  let best: { offset: number; trackDist: number } | undefined;
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1],
      b = route[i];
    if (!a.geoPos || !b.geoPos || ![...point, ...a.geoPos, ...b.geoPos].every(Number.isFinite)) continue;
    const sx = 111320 * Math.cos((point[1] * Math.PI) / 180),
      sy = 111320;
    const ax = (a.geoPos[0] - point[0]) * sx,
      ay = (a.geoPos[1] - point[1]) * sy;
    const dx = (b.geoPos[0] - a.geoPos[0]) * sx,
      dy = (b.geoPos[1] - a.geoPos[1]) * sy;
    const t = Math.max(0, Math.min(1, -(ax * dx + ay * dy) / (dx * dx + dy * dy || 1)));
    const offset = Math.hypot(ax + t * dx, ay + t * dy);
    if (!best || offset < best.offset) best = { offset, trackDist: a.trackDist + t * (b.trackDist - a.trackDist) };
  }
  return best && best.offset <= 1852 ? best : undefined;
}

export async function addRouteDocuments(
  flights: briefgFlowFlightStruct[],
  db: DatabaseService,
  fs: FileSystemService,
  toolIds: number[]
): Promise<briefgFlowFlightStruct[]> {
  const ids = [...new Set(toolIds.filter(Number.isSafeInteger))];
  const points = flights.flatMap((f) => f.route.flatMap((r) => (r.geoPos ? [r.geoPos] : [])));
  if (!ids.length || !points.length) return flights;
  const airportCodes = [
    ...new Set(flights.flatMap((f) => f.route.map((r) => r.desig)).filter((code) => /^[A-Z]{4}$/.test(code))),
  ];
  const airportFilter = airportCodes.map((code) => `s.content LIKE '%${code}%'`).join(" OR ") || "0";
  const minX = Math.min(...points.map((p) => p[0])) - 0.05,
    maxX = Math.max(...points.map((p) => p[0])) + 0.05;
  const minY = Math.min(...points.map((p) => p[1])) - 0.05,
    maxY = Math.max(...points.map((p) => p[1])) + 0.05;
  try {
    const rows = await db.selectProcesses(
      [...DOC_TEXT_SEARCH_PROCESSES],
      `SELECT s.content, s.address, s.survey, s.toolId,
      g.minX, g.minY, g.maxX, g.maxY FROM search_index s LEFT JOIN demo_index g ON g.PK = s.id
      WHERE s.toolId IN (${ids.join(",")}) AND s.priority = 0
      AND ((g.maxX >= ${minX} AND g.minX <= ${maxX} AND g.maxY >= ${minY} AND g.minY <= ${maxY})
        OR (g.PK IS NULL AND (${airportFilter})))
      AND NOT EXISTS (SELECT 1 FROM search_index child WHERE child.toolId = s.toolId AND child.priority = 0
        AND child.address LIKE s.address || '.%')`
    );
    const cache = new Map<string, Promise<string>>();
    // First find the documents of every flight and start their page reads together:
    // on web each one is a request. Then take the pages in the old order.
    const wanted = flights.map((flight) => {
      const picked: { row: any; key: string; trackDist: number }[] = [];
      for (const row of rows) {
        const key = `${row.toolId}:${row.address}`;
        // Country-wide and general documents do not belong beside a route point.
        if (row.maxX - row.minX > 0.2 || row.maxY - row.minY > 0.2) continue;
        const located = [row.minX, row.minY, row.maxX, row.maxY].every((v) => v != null && Number.isFinite(v));
        const waypoint = flight.route.find(
          (r) =>
            /^[A-Z]{4}$/.test(r.desig) &&
            String(row.content)
              .toUpperCase()
              .split(/[^A-Z0-9]+/)
              .includes(r.desig)
        );
        const position = located
          ? documentRoutePosition([(row.minX + row.maxX) / 2, (row.minY + row.maxY) / 2], flight.route)
          : waypoint
            ? { offset: 0, trackDist: waypoint.trackDist }
            : undefined;
        if (!position) continue;
        const path = row.survey
          ?.split("-+-")[1]
          ?.replace(/\\/g, "/")
          .replace("{x}", "1")
          .replace(/page\d+\.thumb/, "page1.thumb")
          .replace(/page\d+-thumb\.html$/, "page1-thumb.html");
        if (!path) continue;
        if (!cache.has(key))
          cache.set(
            key,
            fs.getDocumentByUrl(path).catch(() => "")
          );
        picked.push({ row, key, trackDist: position.trackDist });
      }
      return picked;
    });
    for (const [i, flight] of flights.entries()) {
      const seen = new Set<string>();
      for (const { row, key, trackDist } of wanted[i]) {
        if (seen.has(key)) continue;
        const html = await cache.get(key);
        if (!html) continue;
        seen.add(key);
        flight.briefingContent.push({
          type: briefgFlowContentEnum.doc,
          title: row.content,
          trackDist,
          parameters: [html, row.content, key, row.address, row.toolId],
        });
      }
      flight.briefingContent.sort((a, b) => a.trackDist - b.trackDist);
    }
  } catch (err) {
    console.warn("flowBriefing: could not load route documents", err);
  }
  return flights;
}
